import React, { useEffect, useState } from "react";
import { Col, Form, FormGroup, FormText, Input, Label, Row, Button } from "reactstrap";
import { MSG_DESTINATION } from "../../../utils/message";
import DestinationFooter from "./common/destinationFooter";
import {
  handleYupValidationErrors,
  yupValidationSchema,
} from "../../../utils/functions";
import ValidationErrorHandling from "../../common/validationErrorHandling";
import DestinationHeader from "./common/destinationHeader";
import Select from "react-select";
import useGet from "../../../services/useGet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'

const DESTINATION_GOHIGHLEVEL = process.env.REACT_APP_INTEGRATION_GOHIGHLEVEL;

const validationSchema = yupValidationSchema({
  goHighLevelApiKey: { required: true, customName: "Api key" },
});

const GoHighLevelForm = ({ destinations, saveIntegration }) => {
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([
    {
      customerEmail: "",
      timezoneSelected: "",
      goHighLevelApiKey: "",
      sendLeadsLimitGoHighLevel: "",
    },
  ]);
  const [errors, setErrors] = useState({});
  const [timezoneList, setTimezoneList] = useState([]);


  // Fetching timezones using the useGet hook
  const {
    callApi: getTimezones,
    data: timezones,
    isLoading: getTimezonesLoader,
  } = useGet({
    endpoint: `common/timezones`,
  });


  useEffect(() => {
    if (destinations) {
      destinations.forEach((dest) => {
        console.log("dest", dest);
        const { integrationCompany, secretKey, leadSentConfig, extraInfo } = dest;

        if (integrationCompany === DESTINATION_GOHIGHLEVEL) {
          if (extraInfo && extraInfo.length > 0) {
            // If extraInfo exists, map it to the customers state
            const updatedCustomers = extraInfo.map((info) => ({
              customerEmail: info.customerEmail || "",
              timezoneSelected: info.timezoneSelected || "",
              goHighLevelApiKey: info.secretKey || secretKey, // Use info.secretKey if available, else fallback to secretKey
              sendLeadsLimitGoHighLevel: info.sendLeadsLimit || leadSentConfig?.sentLimit, // Use info.sendLeadsLimit if available, else fallback to leadSentConfig.sentLimit
            }));

            setCustomers(updatedCustomers);
          } else {
            // If no extraInfo, initialize with a single customer
            setCustomers([
              {
                // customerEmail: "",
                // timezoneSelected: "",
                goHighLevelApiKey: secretKey,
                sendLeadsLimitGoHighLevel: leadSentConfig?.sentLimit,
              },
            ]);
          }
        }
      });
    }
  }, [destinations]);



  useEffect(() => {
    if (timezones) {
      const tzList = timezones.map((tz) => {
        const formattedOffset = `UTC${tz.utc[0] >= 0 ? "+" : ""}${tz.utc[0]}`;
        return {
          label: `${tz.text} ${formattedOffset}`,
          value: tz.utc[0],
        };
      });
      setTimezoneList(tzList);
    }
  }, [timezones]);

  useEffect(() => {
    getTimezones();
  }, []);

  const handleFieldChange = (index, field, value) => {
    const updatedCustomers = [...customers];
    updatedCustomers[index][field] = value;
    setCustomers(updatedCustomers);
  };

  const handleAddCustomer = () => {
    setCustomers([
      ...customers,
      {
        customerEmail: "",
        timezoneSelected: "",
        goHighLevelApiKey: "",
        sendLeadsLimitGoHighLevel: "",
      },
    ]);
  };

  const handleRemoveCustomer = (index) => {
    const updatedCustomers = [...customers];
    updatedCustomers.splice(index, 1);
    setCustomers(updatedCustomers);
  };

  const handleGoHighLevelFormForm = async (e) => {
    e.preventDefault();


    try {
      // Validate each customer object
      for (const customer of customers) {
        await validationSchema.validate(
          { goHighLevelApiKey: customer.goHighLevelApiKey },
          { abortEarly: false }
        );
      }

      setLoading(true);

      if (customers.length > 1) {
        // Multiple customers: Send extraInfo
        const extraInfo = customers.map(customer => ({
          secretKey: customer.goHighLevelApiKey,
          sendLeadsLimit: customer.sendLeadsLimitGoHighLevel,
          customerEmail: customer.customerEmail,
          timezoneSelected: customer.timezoneSelected,
        }));

        await saveIntegration(
          {
            extraInfo,
            integrationCompany: DESTINATION_GOHIGHLEVEL,
          },
          MSG_DESTINATION.SAVE_GOHIGHLEVEL
        );
      } else {
        // Single customer: Send individual data
        const customer = customers[0];

        await saveIntegration(
          {
            secretKey: customer.goHighLevelApiKey,
            sendLeadsLimit: customer.sendLeadsLimitGoHighLevel,
            integrationCompany: DESTINATION_GOHIGHLEVEL,
          },
          MSG_DESTINATION.SAVE_GOHIGHLEVEL
        );
      }

      setErrors({});
    } catch (e) {
      setErrors(handleYupValidationErrors(e));
    } finally {
      setLoading(false);
    }
  };


  return (
    <DestinationHeader
      headerTitle="GoHighLevel"
      headerImage="images/gohighlevel.png"
    >
      <Form onSubmit={handleGoHighLevelFormForm} autoComplete="off">
        <FormGroup>
          <FormText color="info">
            <strong>NOTE:</strong> You can get your API key from Setting - Business Profile
          </FormText>
        </FormGroup>


        {customers.map((customer, index) => (
          <div key={index} style={{ border: "1px solid #ccc", padding: "15px", marginBottom: "10px", borderRadius: "8px" }}>
            <Row>
              {index !== 0 && <>
                <Col lg={6}>
                  <FormGroup>
                    <Label for={`customer_email_${index}`}>Customer Email</Label>
                    <Input
                      id={`customer_email_${index}`}
                      name="customer_email"
                      placeholder="Enter customer email"
                      type="email"
                      value={customer.customerEmail}
                      onChange={(e) => handleFieldChange(index, "customerEmail", e.target.value)}
                      autoComplete="off"
                    />
                    <ValidationErrorHandling error={errors.customerEmail} />
                  </FormGroup>
                </Col>

                <Col lg={6}>
                  <FormGroup>
                    <Label for={`timezone_${index}`}>Customer Timezone</Label>
                    {getTimezonesLoader ? (
                      <p>Loading timezones...</p>
                    ) : (
                      <Select
                        options={timezoneList}
                        onChange={(selectedOption) => handleFieldChange(index, "timezoneSelected", selectedOption.value)}
                        value={timezoneList.find((option) => option.value === customer.timezoneSelected)}
                      />
                    )}
                  </FormGroup>
                </Col>
              </>}

              <Col lg={6}>
                <FormGroup>
                  <Label for={`api_key_${index}`}>API Key</Label>
                  <Input
                    id={`api_key_${index}`}
                    name="api_key"
                    placeholder="Get API key from GoHighLevel Application"
                    type="text"
                    value={customer.goHighLevelApiKey}
                    onChange={(e) => handleFieldChange(index, "goHighLevelApiKey", e.target.value)}
                    invalid={errors.goHighLevelApiKey}
                    autoComplete="off"
                  />
                  <ValidationErrorHandling error={errors.goHighLevelApiKey} />
                </FormGroup>
              </Col>

              <Col lg={6}>
                <FormGroup>
                  <Label for={`send_leads_initially_${index}`}>
                    How Many Leads Do You Want To Send Initially?
                  </Label>
                  <Input
                    id={`send_leads_initially_${index}`}
                    name="send_leads_initially"
                    placeholder="Total lead(s) send initially"
                    type="text"
                    value={customer.sendLeadsLimitGoHighLevel}
                    onChange={(e) => handleFieldChange(index, "sendLeadsLimitGoHighLevel", e.target.value)}
                    autoComplete="off"
                  />
                </FormGroup>
              </Col>
            </Row>

            {index > 0 && (
              <Button
                color="danger"
                onClick={() => handleRemoveCustomer(index)}
                style={{ marginLeft: "4px", marginBottom: "5px", fontSize: "14px", marginTop: "10px" }}
              >
                <FontAwesomeIcon icon={faTrash} style={{ marginRight: "5px" }} /> Delete
              </Button>
            )}
          </div>
        ))}

        <Button
          color="primary"
          onClick={handleAddCustomer}
          style={{ marginLeft: "19px", marginBottom: "20px", fontSize: "14px" }}
        >
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: "2px" }} /> Customer
        </Button>


        <DestinationFooter
          handleSubmit={handleGoHighLevelFormForm}
          loading={loading}
        />
      </Form>
    </DestinationHeader>
  );
};

export default GoHighLevelForm;






// import React, { useEffect, useState } from "react";
// import { Col, Form, FormGroup, FormText, Input, Label, Row } from "reactstrap";
// import { MSG_DESTINATION } from "../../../utils/message";
// import DestinationFooter from "./common/destinationFooter";
// import {
//   handleYupValidationErrors,
//   yupValidationSchema,
// } from "../../../utils/functions";
// import ValidationErrorHandling from "../../common/validationErrorHandling";
// import DestinationHeader from "./common/destinationHeader";
// import moment from "moment-timezone";
// import Select from "react-select";
// import useGet from "../../../services/useGet";

// const DESTINATION_GOHIGHLEVEL = process.env.REACT_APP_INTEGRATION_GOHIGHLEVEL;

// const validationSchema = yupValidationSchema({
//   goHighLevelApiKey: { required: true, customName: "Api key" },
// });

// const GoHighLevelForm = ({ destinations, saveIntegration }) => {
//   const [loading, setLoading] = useState(false);
//   const [goHighLevelApiKey, setGoHighLevelApiKey] = useState("");
//   const [sendLeadsLimitGoHighLevel, setSendLeadsLimitGoHighLevel] = useState(0);
//   const [errors, setErrors] = useState({});
//   const [customerEmail, setCustomerEmail] = useState("");
//   const [timezoneList, setTimezoneList] = useState([]);
//   const [timezoneSelected, setTimezoneSelected] = useState("");





//   // Fetching timezones using the useGet hook
//   const {
//     callApi: getTimezones,
//     data: timezones,
//     isLoading: getTimezonesLoader,
//   } = useGet({
//     endpoint: `common/timezones`,
//   });


//   useEffect(() => {

//     if (timezones) {
//       const tzList = timezones.map((tz) => {
//         const formattedOffset = `UTC${tz.utc[0] >= 0 ? "+" : ""}${tz.utc[0]}`;
//         return {
//           label: `${tz.text} ${formattedOffset}`,
//           value: tz.utc[0],
//         };
//       });
//       setTimezoneList(tzList);
//     }
//   }, [timezones]);

//   useEffect(() => {

//     getTimezones();
//   }, []);



//   useEffect(() => {
//     if (destinations) {
//       for (let i = 0; i < destinations.length; i++) {
//         const dest = destinations[i];
//         console.log("dessst", dest);
//         const { integrationCompany, secretKey, leadSentConfig } = dest;
//         if (integrationCompany === DESTINATION_GOHIGHLEVEL) {
//           setGoHighLevelApiKey(secretKey);
//           setSendLeadsLimitGoHighLevel(leadSentConfig?.sentLimit);

//         }
//       }
//     }
//   }, [destinations]);

//   const handleGoHighLevelFormForm = async (e) => {
//     e.preventDefault();
//     try {
//       await validationSchema.validate(
//         { goHighLevelApiKey },
//         { abortEarly: false }
//       );
//       setLoading(true);
//       await saveIntegration(
//         {
//           secretKey: goHighLevelApiKey,
//           sendLeadsLimit: sendLeadsLimitGoHighLevel,
//           integrationCompany: DESTINATION_GOHIGHLEVEL,
//         },
//         MSG_DESTINATION.SAVE_GOHIGHLEVEL
//       );
//       setErrors({});
//     } catch (e) {
//       setErrors(handleYupValidationErrors(e));
//     } finally {
//       setLoading(false);
//     }
//   };






//   return (
//     // <DestinationHeader
//     //   headerTitle="GoHighLevel"
//     //   headerImage="images/gohighlevel.png"
//     // >
//     //   <Form onSubmit={handleGoHighLevelFormForm} autoComplete="off">
//     //     <FormGroup>
//     //       <FormText color="info">
//     //         <strong> NOTE:</strong>You can get your API key from Setting-&gt;
//     //         Bussiness Profile
//     //       </FormText>
//     //     </FormGroup>
//     //     <Row>
//     //       <Col lg={6}>
//     //         <FormGroup>
//     //           <Label for="api_key">API Key</Label>
//     //           <Input
//     //             id="api_key"
//     //             name="api_key"
//     //             placeholder="Get API key from GoHighLevel Application"
//     //             autocomplete="off"
//     //             type="text"
//     //             defaultValue={goHighLevelApiKey}
//     //             value={goHighLevelApiKey}
//     //             onChange={(e) => setGoHighLevelApiKey(e.target.value)}
//     //             invalid={errors.goHighLevelApiKey}
//     //             autoComplete="off"
//     //           />
//     //           <ValidationErrorHandling error={errors.goHighLevelApiKey} />
//     //         </FormGroup>
//     //       </Col>

//     //       <Col lg={6}>
//     //         <FormGroup>
//     //           <Label for="send_leads_initially">
//     //             How Many Leads Do You Want To Send Initially?
//     //           </Label>
//     //           <Input
//     //             id="send_leads_initially"
//     //             name="send_leads_initially"
//     //             placeholder="Total lead(s) send initially"
//     //             type="text"
//     //             value={sendLeadsLimitGoHighLevel}
//     //             onChange={(e) => setSendLeadsLimitGoHighLevel(e.target.value)}
//     //             autoComplete="off"
//     //             autoFocus
//     //           />
//     //         </FormGroup>
//     //       </Col>
//     //     </Row>

//     //     <DestinationFooter
//     //       handleSubmit={handleGoHighLevelFormForm}
//     //       loading={loading}
//     //     />
//     //   </Form>
//     // </DestinationHeader>

//     <DestinationHeader
//       headerTitle="GoHighLevel"
//       headerImage="images/gohighlevel.png"
//     >
//       <Form onSubmit={handleGoHighLevelFormForm} autoComplete="off">
//         <FormGroup>
//           <FormText color="info">
//             <strong>NOTE:</strong> You can get your API key from Setting - Business Profile
//           </FormText>
//         </FormGroup>
//         <Row>
//           <Col lg={6}>
//             <FormGroup>
//               <Label for="customer_email">Customer Email</Label>
//               <Input
//                 id="customer_email"
//                 name="customer_email"
//                 placeholder="Enter customer email"
//                 type="email"
//                 value={customerEmail}
//                 onChange={(e) => setCustomerEmail(e.target.value)}
//                 autoComplete="off"
//               />
//               <ValidationErrorHandling error={errors.customerEmail} />
//             </FormGroup>
//           </Col>

//           <Col lg={6}>
//             <FormGroup>
//               <Label for="timezone">Customer Timezone</Label>
//               {getTimezonesLoader ? (
//                 <p>Loading timezones...</p>
//               ) : (
//                 <Select
//                   isLoading={getTimezonesLoader}
//                   options={timezoneList}
//                   onChange={(selectedOption) => {
//                     setTimezoneSelected(selectedOption.value);
//                   }}
//                   value={timezoneList.find((option) => option.value === timezoneSelected)}
//                 />
//               )}
//             </FormGroup>
//           </Col>

//           <Col lg={6}>
//             <FormGroup>
//               <Label for="api_key">API Key</Label>
//               <Input
//                 id="api_key"
//                 name="api_key"
//                 placeholder="Get API key from GoHighLevel Application"
//                 type="text"
//                 defaultValue={goHighLevelApiKey}
//                 value={goHighLevelApiKey}
//                 onChange={(e) => setGoHighLevelApiKey(e.target.value)}
//                 invalid={errors.goHighLevelApiKey}
//                 autoComplete="off"
//               />
//               <ValidationErrorHandling error={errors.goHighLevelApiKey} />
//             </FormGroup>
//           </Col>

//           <Col lg={6}>
//             <FormGroup>
//               <Label for="send_leads_initially">
//                 How Many Leads Do You Want To Send Initially?
//               </Label>
//               <Input
//                 id="send_leads_initially"
//                 name="send_leads_initially"
//                 placeholder="Total lead(s) send initially"
//                 type="text"
//                 value={sendLeadsLimitGoHighLevel}
//                 onChange={(e) => setSendLeadsLimitGoHighLevel(e.target.value)}
//                 autoComplete="off"
//                 autoFocus
//               />
//             </FormGroup>
//           </Col>
//         </Row>

//         <DestinationFooter
//           handleSubmit={handleGoHighLevelFormForm}
//           loading={loading}
//         />
//       </Form>
//     </DestinationHeader>



//   );
// };

// export default GoHighLevelForm;










